import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { InjectionNames, OriginalPaletteProvider } from '../bpmn-js/bpmn-js';
import { AlertComponent } from '../common/alert/alert.component';
import { FlowSheetEventsHandler } from '../flowsheet/flowsheet-events/flowsheet.events.handler';
import { RenameModelComponent } from '../flowsheet/rename-model/rename-model.component';
import { SaveFlowSheetComponent } from '../flowsheet/save-flowsheet/save-flowsheet.component';
import { DataSrc, FlowSheet } from '../models/flowsheet/flowsheet.model';
import { CustomPaletteProvider } from '../props-provider/CustomPaletteProvider';
import { FlowSheetService } from '../services/flowsheet-service';
import { GlobalService } from '../services/global.service';
import { MLService } from '../services/ml-service';
import { ConnectorSpecificationComponent } from './connector-specification/connector-specification.component';
import { DataSourceManagerComponent } from './data-source-manager/data-source-manager.component';
import CustomModeler from './index';
import { ConfirmModelComponent } from '../common/confirm-model/confirm-model.component';
import { TenantService } from '../services/tenant.service';
import { catchError, switchMap } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { FlowSheetRunDetailsComponent } from '../flowsheet/flowsheet-run-details/flowsheet-run-details.component';

@Component({
  selector: 'app-flowsheet',
  templateUrl: './flowsheet-builder.component.html',
  styleUrls: ['./flowsheet-builder.component.css'],
  host: {
    '(document:click)': 'onClick($event)',
  }
})
export class FlowsheetBuilderComponent implements OnInit {
  isLoading = false;
  modeler;
  activPallet = "Basic";
  palletData;
  dataToPass: any = [];
  modelSvgIcons: any = [];
  bpmnDiagram: any;
  element: any;
  isEdit: boolean = false;
  flowSheetId: number;
  editMode = false;
  viewOnly: boolean = false;
  isReadyForNewRun = false;
  flowsheetShow = false;
  flowSheet: FlowSheet = new FlowSheet();
  isReadyForTerminiate = false;
  isAuthorized = false;
  showContextMenu = false;
  posX = 0;
  posY = 0;
  modelToRename: any = {};
  renameModelInfo: any = {};
  getRouteParams;
  searchText: string;
  filteredPalletData: any;

  constructor(private readonly http: HttpClient,
    public globals: GlobalService,
    public flowSheetService: FlowSheetService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    public mlService: MLService,
    public dialog: MatDialog,
    public router: Router,
    public route: ActivatedRoute,
    private readonly _eref: ElementRef,
    public flowSheetHandler: FlowSheetEventsHandler,
    public tenantService: TenantService
  ) {
  }

  ngOnInit() {
    this.isLoading = true;
    this.getModelListData();
  }

  getModelListData(){
    this.mlService.getModelList().pipe(
      catchError(() => {
        this.globals.showAlert(AlertComponent, { success: false, message: "Error getting models data" });
        this.isLoading = false;
        return EMPTY;
      }),
      switchMap((data: any) => {
        localStorage.setItem('palletData', JSON.stringify(data));
        this.palletData = data;
        this.assignPalletCopy();
        this.modeler = this.createCustomModeler();
        return of(data);
      }),
      switchMap((data) => { this.getFlowsheetDetails(); return of(data); }),
      switchMap((data) => { this.validateIfReadyForNewRun(); return of(data); }),
      ).subscribe((data: any) => {
        if (data.flowSheetRuns && data.flowSheetRuns.length > 0 && data.flowSheetRuns[0].status.toLowerCase() === "in progress") {
          this.isReadyForTerminiate = true;
          this.isReadyForNewRun = false;
        }
        else {
          this.isReadyForNewRun = true;
        }
        this.isLoading = false;
      }, (error: Error) => {
        this.globals.showAlert(AlertComponent, { success: false, message: "Failed to check if Flowsheet can be run." });
        this.isLoading = false;
        this.isReadyForNewRun = false;
      });
  }


  assignPalletCopy() {
    this.filteredPalletData = Object.assign([], this.palletData);
  }

  searchPallet(data: string) {
    if (data) {
      this.searchText = data;
      this.filteredPalletData = Object.assign([], this.palletData)
      .filter((element) =>
        element.modelData.some((subElement) => subElement.modelName.toLowerCase().includes(this.searchText.toLowerCase())))
      .map(element => {
        return {...element, modelData: element.modelData.filter(subElement => subElement.modelName.toLowerCase().includes(this.searchText.toLowerCase())) };
        
      });
    }
    else {
      this.assignPalletCopy();
    }    
  }

  getFlowsheetDetails() {
    this.isLoading = true;
    return this.route.params.subscribe(
      (params: Params) => {
        this.flowsheetShow = params.id;
        if (this.flowsheetShow) {
          this.isAuthorized = params.isAuthorized === 'true';
          this.editFlowsheet(params.id, params.isEdit);
          return of(params);
        } else {
          this.load();
          this.seteventBus(this);
          this.isLoading = false;
        }
        return of(params);
      }, (error: Error) => {
        this.globals.showAlert(AlertComponent, { success: false, message: "Error getting models data" });
        this.isLoading = false;
      }
    );
  }

  editFlowsheet(parameterId, isEdit) {
    this.isEdit = (isEdit === 'true' && this.isAuthorized === true);
    this.viewOnly = isEdit === 'false';
    this.flowSheetId = parameterId;
    this.flowSheetService.getFlowData(parameterId).subscribe((data: any): void => {
      this.flowSheet = data;
      this.validateIfReadyForNewRun();
      try {
        this.modeler.importXML(this.flowSheet.bpmnXml)
        .then(({ warnings }) => {
          this.modeler.get('canvas').zoom('fit-viewport', 'auto');
        });
        this.seteventBus(this);
      } catch (err) {
        this.globals.showAlert(AlertComponent, { success: false, message: err.message });
      }
      this.isLoading = false;
    }, (error: Error) => {
      this.globals.showAlert(AlertComponent, { success: false, message: "Error in getting flowsheet data" });
      this.isLoading = false;
    });
  }

  validateIfReadyForNewRun() {
    this.isLoading = true;
    this.flowSheetService.getFlowsheetRunsList(this.flowSheet.flowSheetId).subscribe((data: any) => {
      return of(data);
    }, (error: Error) => {
      this.globals.showAlert(AlertComponent, { success: false, message: "Failed to check if Flowsheet can be run." });
      this.isLoading = false;
      this.isReadyForNewRun = false;
    });
  }
  inEditMode() {
    this.isEdit = true;
    this.viewOnly = false;
    this.seteventBus(this);
  }

  onClick() {
    if (this.showContextMenu) // or some similar check
      this.showContextMenu = false;
  }

  seteventBus(thisObj) {
    let that = thisObj;
    let eventBus = thisObj.modeler.get('eventBus');
    eventBus.on(['element.click', 'element.contextmenu'], 10000, function (context) {
      if (thisObj.viewOnly) {
        return false;
      }
    });
    eventBus.on('create.start', function (element) {
      // Set uniqueId in model element
      that.setUniqueIdForModel(element);
    });
    eventBus.on('directEditing.activate', function (elem) {
      // Cancel direct editing to override dblclick event properly
      let directEditing = that.modeler.get('directEditing');
      directEditing.cancel();
    });
    eventBus.on('shape.added', function (elem) {
      that.processShapeAddedEvent(elem);
    });
    eventBus.on('shape.removed', function (elem) {
      that.removeModel(elem.element.modelUniqueId);
    });
    eventBus.on('commandStack.connection.create.postExecuted', function (elem) {
      that.validateConnetion(elem.context);
    });
    eventBus.on('connection.added', function (elem) {
      if (elem.element.source.modelUniqueId != elem.element.target.modelUniqueId) {
        if (!elem.element.businessObject.sourceRef && !elem.element.businessObject.targetRef) {
          that.updateConnection(elem.element);
        }
      }
    });
    eventBus.on('connection.removed', function (elem) {
      let conns = elem.element.businessObject.$parent.flowElements.filter(e => e.$type == 'bpmn:SequenceFlow');
      if (conns.filter(e => e.id != elem.element.id
        && e.sourceRef.$attrs.modelUniqueId == elem.element.source.modelUniqueId
        && e.targetRef.$attrs.modelUniqueId == elem.element.target.modelUniqueId).length == 0) {
        that.removeConnection(elem.element);
      }
    });
    eventBus.on('element.dblclick', 10000, function (context) {
      that.processDblClkEvent(context, that);
      context.stopPropagation();
    });
    eventBus.on("element.contextmenu", event => {
      that.processContextMenuEvent(event);
    });
  }

  processShapeAddedEvent(elem) {
    if (this.flowSheet.models.findIndex(c => c.modelUniqueId == elem.element.businessObject.$attrs.modelUniqueId) == -1) {
      this.addModel(elem.element);
    } else {
      elem.element['modelId'] = Number(elem.element.businessObject.$attrs.modelId);
      elem.element['modelImg'] = elem.element.businessObject.$attrs.modelImg;
      elem.element['modelName'] = elem.element.businessObject.$attrs.modelName;
      elem.element['modelType'] = 'custom';
      elem.element['modelUniqueId'] = elem.element.businessObject.$attrs.modelUniqueId;
      elem.element['modelVersion'] = elem.element.businessObject.$attrs.modelVersion;
    }
  }

  processContextMenuEvent(event) {
    if (event.element.type === "bpmn:Task") {
      this.modelToRename = event;
      this.posX = event.originalEvent.pageX;
      this.posY = event.originalEvent.pageY;
      let entireModeler = this.modeler.getDefinitions();
      this.renameModelInfo = { name: event.gfx.parentNode.children[0].textContent, modeler: entireModeler };
      this.showContextMenu = true;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  renameModel() {
    this.globals.openModelWindow(this.changeModelName, this, RenameModelComponent, '40%', 'custom-modalbox', this.renameModelInfo);
  }

  changeModelName(thisRef: FlowsheetBuilderComponent, newModelName: any) {
    let oldName = thisRef.modelToRename.gfx.parentNode.children[0].textContent;
    thisRef.flowSheet.models.forEach((m) => {
      if (m.modelUniqueId == oldName) {
        m.modelUniqueId = newModelName;
      }
      m.srcModels.forEach((m1, index) => {
        m.srcModels[index] = m1 == oldName ? newModelName : m1;
      });
      m.sinkModels.forEach((m1, index) => {
        m.sinkModels[index] = m1 == oldName ? newModelName : m1;
      });
      m.inputParameters.forEach((p) => {
        if (p.mappedDataSource == DataSrc.model && p.mappedDataSourceId == oldName) {
          p.mappedDataSourceId = newModelName;
        }
      });
    });
    let svgTextElement = thisRef.getTextNodeElement();
    thisRef.modelToRename.element.modelUniqueId = newModelName;
    thisRef.modelToRename.element.businessObject.$attrs.modelUniqueId = newModelName;
    svgTextElement[0].childNodes[0].nodeValue = newModelName;
  }

  getTextNodeElement() {
    return this.modelToRename.gfx.parentNode.children[0].getElementsByTagName("text");
  }

  validateConnetion(connElem) {
    // Check if the source and target of the connection element is same model
    let isSameModel = connElem.source.modelUniqueId == connElem.target.modelUniqueId;

    let conns = connElem.parent.children.filter(e => e.type == 'bpmn:SequenceFlow');
    // Check If save connection already exists
    let connectionAlreadyExist = conns.filter(e => e.id != connElem.connection.id
      && e.source.modelUniqueId == connElem.source.modelUniqueId
      && e.target.modelUniqueId == connElem.target.modelUniqueId).length > 0;

    // Check if the connection is creating any circle in execution cycle
    let srcModelsTrace = [connElem.source.modelUniqueId];
    this.getSrcModelUniqueIdRecursive(conns, srcModelsTrace, connElem.source.modelUniqueId);
    let isCausingCircle = srcModelsTrace.findIndex(m => m == connElem.target.modelUniqueId) != -1;

    if (isSameModel || connectionAlreadyExist || isCausingCircle) {
      this.removeElementFromRegistry(connElem.connection.id);
    }
  }

  getSrcModelUniqueIdRecursive(conns: any, srcModelsTrace: string[], modelUniqueId: string) {
    let conn = conns.find(c => c.target.modelUniqueId == modelUniqueId);
    if (conn != null && srcModelsTrace.findIndex(m => m == conn.source.modelUniqueId) == -1) {
      srcModelsTrace.push(conn.source.modelUniqueId);
      this.getSrcModelUniqueIdRecursive(conns, srcModelsTrace, conn.source.modelUniqueId);
    }
  }

  removeElementFromRegistry(elementId) {
    let elementRegistry = this.modeler.get('elementRegistry');
    let modeling = this.modeler.get('modeling');
    let element = elementRegistry.get(elementId);
    modeling.removeElements([element]);
  }

  addModel(element) {
    this.isLoading = true;
    this.flowSheetHandler.addModel(this, element.modelUniqueId, element.modelId, element.modelName, element.modelVersion);
  }

  setUniqueIdForModel(element) {
    let maxId: number = 0;
    this.modeler.get('elementRegistry');

    let b1 = this.modeler.getDefinitions();
    let c1;
    if (b1.diagrams[0].plane.planeElement) {
      c1 = b1.diagrams[0].plane.planeElement.filter((m) => { return m.bpmnElement.$attrs.modelId == element.shape.modelId });

      if (c1.length > 0) {
        let args = c1.map(function (m) {
          let value = m.bpmnElement.$attrs.modelUniqueId.replace(element.shape.modelName + "_", "");
          return isNaN(value) ? 0 : value;
        });
        maxId = Math.max(...args);
      }
    }

    element.shape['modelUniqueId'] = element.shape.modelName + "_" + ("0" + (maxId + 1)).slice(-2);
  }

  updateConnection(element) {
    // source and target models in new connection
    let newSrcModel = element.source;
    let newSinkModel = element.target;
    // source and target model instances in flowsheet
    let srcModel = this.flowSheet.models.find(s => s.modelUniqueId == newSrcModel.modelUniqueId);
    let sinkModel = this.flowSheet.models.find(s => s.modelUniqueId == newSinkModel.modelUniqueId);
    // add src and sink relation if not already exist
    if (srcModel.sinkModels.findIndex(p => p == sinkModel.modelUniqueId) == -1) {
      srcModel.sinkModels.push(newSinkModel.modelUniqueId);
    }
    if (sinkModel.srcModels.findIndex(p => p == srcModel.modelUniqueId) == -1) {
      sinkModel.srcModels.push(newSrcModel.modelUniqueId);
    }
  }

  removeConnection(element) {
    // source and target models in removed connection
    let oldSrcModel = element.source;
    let oldSinkModel = element.target;
    // source and target model instances in flowsheet
    let srcModel = this.flowSheet.models.find(s => s.modelUniqueId == oldSrcModel.modelUniqueId);
    let sinkModel = this.flowSheet.models.find(s => s.modelUniqueId == oldSinkModel.modelUniqueId);
    // remove src and sink relation
    srcModel.sinkModels.splice(
      srcModel.sinkModels.findIndex(m => m == oldSinkModel.modelUniqueId)
      , 1);

    this.flowSheetHandler.removeSrcModelParameterMappings(sinkModel, oldSrcModel.modelUniqueId);
    sinkModel.srcModels.splice(
      sinkModel.srcModels.findIndex(m => m == oldSrcModel.modelUniqueId)
      , 1);
  }

  removeModel(modelUniqueId) {
    this.flowSheetHandler.removeModel(this.flowSheet, modelUniqueId);
  }

  createCustomModeler(): any {
    return new CustomModeler({
      container: '#canvas',
      width: '100%',
      height: '540px',
      additionalModules: [
        { [InjectionNames.originalPaletteProvider]: ['type', OriginalPaletteProvider] },
        { [InjectionNames.paletteProvider]: ['type', CustomPaletteProvider] },
      ]
    });
  }

  changeSource(event) {
    event.target.src = 'assets/bpmn/defaultModel.svg';
  }

  processDblClkEvent(context, thisRef) {
    this.element = context.element;
    if (this.element.type == "bpmn:SequenceFlow") {
      let srcModel = this.flowSheet.models.find(m => m.modelUniqueId == this.element.businessObject.sourceRef.$attrs.modelUniqueId);
      let sinkModel = this.flowSheet.models.find(m => m.modelUniqueId == this.element.businessObject.targetRef.$attrs.modelUniqueId);
      thisRef.dataToPass = {
        'srcModel': srcModel,
        'sinkModel': sinkModel,
        'readOnly': this.viewOnly
      };
      thisRef.globals.openModelWindow(null, thisRef, ConnectorSpecificationComponent, '80%', 'custom-modalbox', thisRef.dataToPass);
    } else if (this.element.type == "bpmn:Task") {
      thisRef.dataToPass = {
        'model': this.flowSheet.models.find(m => m.modelUniqueId == this.element.modelUniqueId),
        'readOnly': this.viewOnly
      };
      thisRef.globals.openModelWindow(null, thisRef, DataSourceManagerComponent, '80%', 'custom-modalbox', thisRef.dataToPass);
    }
    
    return false;
  }

  selectPalleteType(paletteType) {
    this.activPallet = paletteType;
  }

  handleError(err: any) {
    if (err) {
      this.globals.showAlert(AlertComponent, { success: false, message: "Ups, error, please reload the page" });
    }
  }

  selectModel(modelName) {
    let element: HTMLElement = document.querySelector("[data-action='create." + modelName + "-task']") as HTMLElement;
    element.click();
  }

  canvasZoomIn() {
    this.modeler.get('zoomScroll').stepZoom(1);
  }
  canvasZoomOut() {
    this.modeler.get('zoomScroll').stepZoom(-1);
  }
  canvasZoomReset() {
    this.modeler.get('zoomScroll').reset();
  }

  load() {
    let blankBpmnXml = "<?xml version=\"1.0\" encoding=\"UTF-16\"?>\n<bpmn:definitions xmlns:xsi=\"http://www.w3.org/2001/XMLSchema-instance\" xmlns:bpmn=\"http://www.omg.org/spec/BPMN/20100524/MODEL\" xmlns:bpmndi=\"http://www.omg.org/spec/BPMN/20100524/DI\" id=\"Definitions_1\" targetNamespace=\"http://bpmn.io/schema/bpmn\">\n  <bpmn:process id=\"Process_1\" isExecutable=\"false\" />\n  <bpmndi:BPMNDiagram id=\"BPMNDiagram_1\">\n    <bpmndi:BPMNPlane id=\"BPMNPlane_1\" bpmnElement=\"Process_1\" />\n  </bpmndi:BPMNDiagram>\n</bpmn:definitions>\n";
    try {
      this.modeler.importXML(blankBpmnXml)
      .then(({ warnings }) => {
        this.modeler.get('canvas').zoom('fit-viewport', 'auto');
      });
    } catch (err) {
      this.globals.showAlert(AlertComponent, { success: false, message: err.message });
    }
  }

  setFlowSheetData(thisRef: FlowsheetBuilderComponent, data: FlowSheet) {
    thisRef.isLoading = true;
    if (thisRef.flowSheet.flowSheetId === 0 && thisRef.flowSheet.name === '') {
      thisRef.flowSheet.name = data.name;
      thisRef.flowSheet.description = data.description;
      thisRef.flowSheet.refineryName = thisRef.tenantService.getCurrentTenant().tenantRefinery;
      thisRef.flowSheet.createdBy = thisRef.globals.loggedInUser;
      thisRef.flowSheet.lastUpdatedBy = thisRef.globals.loggedInUser;
      thisRef.flowSheet.bpmnXml = thisRef.bpmnDiagram.xml;
      thisRef.flowSheet.tenantId = thisRef.tenantService.getCurrentTenant().tenantId;
    }
    else {
      thisRef.flowSheet.refineryName = thisRef.tenantService.getCurrentTenant().tenantRefinery;
      thisRef.flowSheet.lastUpdatedBy = thisRef.globals.loggedInUser;
      thisRef.flowSheet.bpmnXml = thisRef.bpmnDiagram.xml;
      thisRef.flowSheet.tenantId = thisRef.tenantService.getCurrentTenant().tenantId;
    }

    thisRef.flowSheetService.saveFlowSheet(thisRef.flowSheet).subscribe((data: any): void => {
      thisRef.flowSheet.flowSheetId = data;
      thisRef.editMode = true;
      thisRef.isReadyForNewRun = true;
      thisRef.globals.showAlert(AlertComponent, { success: true, message: "Saved Successfully" });
      thisRef.isLoading = false;
    }, (error: Error) => {
      thisRef.globals.showAlert(AlertComponent, { success: false, message: "Error in Saving, please try again." });
      thisRef.isLoading = false;
    });
  }

  async save() {
    if (this.flowSheet.models.length !== 0) {
      try {
        this.bpmnDiagram = await this.modeler.saveXML({ format: true });
        this.flowSheetHandler.calculateInitialModels(this.flowSheet);

        if (this.flowSheet.initialModelUniqueIds.length == 0) {
          this.globals.showAlert(AlertComponent, { success: false, message: "Failed to trace starting models. Please have atleast 1 model as starting model." });
        }

        this.saveFlowSheet();

      } catch (err) {
        this.globals.showAlert(AlertComponent, { success: false, message: "Could not save flowsheet diagram." });
      }
    } else {
      this.globals.showAlert(AlertComponent, { success: false, message: "No models added to save." });
    }
  }

  saveFlowSheet() {
    if (this.flowSheet.flowSheetId === 0 && this.flowSheet.name === '') {
      this.globals.openModelWindow(this.setFlowSheetData, this, SaveFlowSheetComponent, '40%', 'custom-modalbox', '');
    }
    else {
      this.setFlowSheetData(this, null);
    }
  }

  validateBeforeRun() {
    if (this.flowSheet.models.length > 0) {
      this.validateInputParameters(0);
    }
    else {
      this.globals.showAlert(AlertComponent, { success: false, message: "New run request failed. There is no model present in flowsheet to run." });
    }
  }

  validateInputParameters(index) {
    // check which models still have unmapped parameters
    let unmappedModels = this.flowSheet.models.filter(m => m.inputParameters
      .findIndex(p => p.mappedDataSource == DataSrc.user && p.data.parameterDefaultValue == null) >= 0);

    if (unmappedModels.length > 0) {
      this.globals.showAlert(AlertComponent, { success: false, message: "Not all input parameters mapped for model " + unmappedModels[0].modelUniqueId + "." });
    }
    else {
      this.initiateNewRun();
    }
  }

  initiateNewRun() {
    this.isLoading = true;
    let runReqData = { flowSheetId: this.flowSheet.flowSheetId };
    this.flowSheetService.initiateNewRun(runReqData).subscribe((data: any): void => {
      this.globals.showAlert(AlertComponent, { success: true, message: "New run request has been submitted successfully." });
      this.isReadyForNewRun = false;
      this.isReadyForTerminiate = true;
      this.isLoading = false;
    }, (error: Error) => {
      this.globals.showAlert(AlertComponent, { success: false, message: "New run request failed. Please check models parameters." });
      this.isLoading = false;
    });
  }

  openRunDetails() {
    this.isLoading = true;
   
    this.flowSheetService.getFlowsheetRunsList(this.flowSheet.flowSheetId).subscribe((data: any): void => {
      if (data.flowSheetRuns && data.flowSheetRuns.length > 0) {        
        let tenantIdDetails = { tenantId: this.tenantService.getCurrentTenant().tenantId };
        let flowsheetData = { ...this.flowSheet, ...tenantIdDetails }
        this.dialog.open(FlowSheetRunDetailsComponent, {
          width: '1100px',
          panelClass: 'details-modalbox',
          data: flowsheetData
        });
      }
      else {
        this.globals.showAlert(AlertComponent, { success: false, message: "No run details are available." });
      }
      this.isLoading = false;
    }, (error: Error) => {
      this.globals.showAlert(AlertComponent, { success: false, message: "Run details check failed." });
      this.isLoading = false;
    });
  }

  terminateRun() {
    let dataToPass = {
      title: 'Delete',
      isDelete: false,
      message: 'Do you really want to terminate the letest run request of this flowsheet?'
    };
    this.globals.openModelWindow(this.terminateRunConfirm, this, ConfirmModelComponent, '450px', 'confirm-modalbox', dataToPass);
  }

  terminateRunConfirm(thisRef: FlowsheetBuilderComponent) {
    thisRef.isLoading = true;
    
    thisRef.flowSheetService.terminateFlowsheetRun(thisRef.flowSheet.flowSheetId).subscribe((data: any): void => {
      if (data === "inprogress") {
        thisRef.globals.showAlert(AlertComponent, { success: true, message: "Flowsheet latest run terminated successfully!" });
      } else {
        thisRef.globals.showAlert(AlertComponent, { success: false, message: "Flowsheet run is already completed, it can't be terminate" });
      }
      thisRef.isReadyForTerminiate = false;
      thisRef.isReadyForNewRun = true;
      thisRef.isLoading = false;
    }, (error: Error) => {
      thisRef.globals.showAlert(AlertComponent, { success: false, message: "Something went wrong!" });
      thisRef.isLoading = false;
    });
  }

}
